import { VeivoVehicleDocuments } from "@/redux/apis/vehicle/veivoApi";
import { Vehicle } from "@/redux/slices/vehicle/types";

export type VeivoDocumentState = "verified" | "expiring" | "expired" | "missing" | "not-provided";

export interface VeivoDocumentRow {
  required: VeivoVehicleDocuments["requiredDocuments"][number];
  document: VeivoVehicleDocuments["documents"][number] | null;
  state: VeivoDocumentState;
  /** The date the badge refers to, when there is one. */
  expiryDate: string | null;
}

/**
 * One row per document the country requires (or offers), with its state.
 *
 * The expiry badge comes from the backend (`vehicle.documents[].expiryStatus`),
 * judged in the vehicle's own jurisdiction with the same 42-day window as the
 * tick — never recomputed here. Veivo's document is matched to the vehicle's
 * legacy document of the same type.
 */
export const buildVeivoDocumentRows = (view: VeivoVehicleDocuments, vehicle: Vehicle): VeivoDocumentRow[] =>
  view.requiredDocuments.map((required) => {
    const document = view.documents.find((doc) => doc.docType === required.docType) ?? null;
    if (!document) {
      return { required, document: null, state: required.optional ? "not-provided" : "missing", expiryDate: null };
    }

    const legacy = vehicle.documents.find((doc) => doc.documentType === required.documentType);
    const expiryStatus = legacy?.expiryStatus ?? "none";
    const state: VeivoDocumentState = expiryStatus === "expired" ? "expired" : expiryStatus === "expiring" ? "expiring" : "verified";
    return { required, document, state, expiryDate: legacy?.expiryDate ?? document.expiryDate };
  });

/** Documents worth a banner: required ones that are expired or expiring. */
export const needsAttention = (rows: VeivoDocumentRow[]) =>
  rows.filter((row) => !row.required.optional && (row.state === "expired" || row.state === "expiring"));
